// Copyright 2018 Sorama B.V.

import { autoinject } from "aurelia-framework";
import {
    AudioStreamProperties,
    MeasurementStatus,
    StreamPropertiesSet,
    VideoStreamProperties,
} from "datatypes/SoramaLocalApi";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { StreamMeasurementServiceError } from "errors/StreamMeasurementServiceError";
import { StringExtensions } from "extensions/StringExtensions";
import { ILocalApi } from "libraries/WebApi/ILocalApi";
import { IWebRequestHandler, WebRequestPriority } from "libraries/WebRequestHandler/IWebRequestHandler";
import { ExtendedLifestyleServiceBase } from "services/Lifestyle/ExtendedLifestyleServiceBase";
import { IStreamMeasurementService } from "services/Measurement/Internal/IStreamMeasurementService";

@autoinject
export class StreamMeasurementService
    extends ExtendedLifestyleServiceBase<string[]>
    implements IStreamMeasurementService
{
    private readonly webRequestHandler: IWebRequestHandler;
    private readonly localApi: ILocalApi;
    private readonly recordCall: string = "/Record/{0}?start={1}&duration={2}";

    private streamMeasurementIds: string[];

    private errorAction: (e: StreamMeasurementServiceError) => void;

    public isStreamMeasurementActive = false;
    public measurementState: { key: string; state: MeasurementStatus }[] = [];

    constructor(webRequestHandler: IWebRequestHandler, localApi: ILocalApi) {
        super(5000);
        this.localApi = localApi;
        this.webRequestHandler = webRequestHandler;
    }

    public create(
        properties: (AudioStreamProperties | VideoStreamProperties)[],
        errorAction: (e: StreamMeasurementServiceError) => void
    ): Promise<string[]> {
        if (this.streamMeasurementIds)
            return Promise.reject<string[]>(
                new StreamMeasurementServiceError("Only one stream measurement may be active at any time")
            );

        return this.localApi.stream.then((uri) => {
            return this.webRequestHandler
                .createPostRequest<string[]>(uri, <StreamPropertiesSet>{ properties: properties })
                .send()
                .then((responseMessage) => {
                    const ids = responseMessage.content;

                    this.streamMeasurementIds = ids;
                    this.isStreamMeasurementActive = true;
                    this.errorAction = errorAction;

                    super.addKey(ids);
                    return ids;
                })
                .catch(this.onErrorReceived);
        });
    }

    public cancel(): Promise<void> {
        if (!this.streamMeasurementIds)
            // No measurement active
            return Promise.resolve();

        return this.localApi.stream.then((uri) => {
            return this.webRequestHandler
                .createDeleteRequest<void>(uri, this.streamMeasurementIds)
                .send()
                .catch() // Errors here mean that the measurement was already terminated, swallow exception
                .then(() => {
                    this.onMeasurementCancelled();
                });
        });
    }

    public record(
        storageId: string,
        startMS: number,
        durationMS: number,
        progressCallback?: (progress: number) => void
    ): Promise<void> {
        if (!this.streamMeasurementIds)
            return Promise.reject<void>(new StreamMeasurementServiceError("No active stream measurement"));

        return this.localApi.stream.then((uri) => {
            const apiCall =
                uri + StringExtensions.format(this.recordCall, storageId, startMS.toString(), durationMS.toString());
            let request = this.webRequestHandler.createPostRequest<string[]>(
                apiCall + (progressCallback ? "&withProgress=true" : ""),
                this.streamMeasurementIds
            );

            if (progressCallback) {
                request = request.withProgress((number) => progressCallback(number));
            }

            return request
                .send()
                .then((x) => {})
                .catch(this.onErrorReceived);
        });
    }

    protected onErrorReceived(error: HttpResponseMessageError): never {
        throw new StreamMeasurementServiceError(error.message, error.statusCode);
    }

    protected onError(key: string[], error: Error) {
        if (this.errorAction) {
            this.errorAction(new StreamMeasurementServiceError(error.message));
        }

        this.onMeasurementCancelled();
    }

    protected onMeasurementCancelled() {
        // Remove key from the parent class
        super.removeKey(this.streamMeasurementIds);

        // Set current stream measurement to null
        this.streamMeasurementIds = null;
        this.isStreamMeasurementActive = false;
        this.measurementState.splice(0, this.measurementState.length);
    }

    protected onUpdate(key: string[]): Promise<void> {
        return this.localApi.stream.then((uri) => {
            return this.webRequestHandler
                .createPutRequest<{ [key: string]: MeasurementStatus }>(uri, key)
                .withPriority(WebRequestPriority.HighPriority)
                .send()
                .then((x) => {
                    const content = x.content;
                    for (const key of Object.getOwnPropertyNames(content)) {
                        const state = { key: key, state: content[key] };
                        const existing = this.measurementState.findIndex((x) => x.key === key);
                        if (existing !== -1) {
                            this.measurementState.splice(existing, 1, state);
                        } else {
                            this.measurementState.push(state);
                        }
                    }
                });
        });
    }
}
